/**
 * Authentication Configuration (Azure AD)
 *
 * `azureADConfig` is a lazy Proxy that defers reading `env.*` until the
 * first property access. The previous shape — a top-level `const = {
 * tenantId: env.azureAdTenantId, ... }` — caused
 * `Cannot read properties of undefined (reading 'azureAdTenantId')` at
 * module init in production builds because Rolldown's chunk init order
 * sometimes evaluates auth-config.ts before env.ts has finished loading.
 * Every chunk loads fine (HTTP 200) but the binding hadn't been
 * populated yet at the moment the const initializer ran.
 *
 * The Proxy keeps the same public API for consumers
 * (`azureADConfig.tenantId`, `azureADConfig.scopes`, etc.) while making
 * the env access happen on first property read instead of module load.
 * By then every chunk is fully evaluated and `env` is bound correctly.
 */

import { env } from './env';

// ============== Azure AD Configuration ==============

export interface AzureADConfig {
  tenantId: string;
  clientId: string;
  redirectUri: string;
  scopes: string[];
}

let _resolved: AzureADConfig | null = null;
function resolveConfig(): AzureADConfig {
  if (_resolved) return _resolved;
  _resolved = {
    tenantId: env.azureAdTenantId,
    clientId: env.azureAdClientId,
    redirectUri: env.azureAdRedirectUri || `${window.location.origin}/auth/callback`,
    // Custom API scope for backend validation + standard OIDC scopes
    scopes: [
      `api://${env.azureAdClientId}/access_as_user`,
      'openid',
      'profile',
      'email',
      'offline_access',
    ],
  };
  return _resolved;
}

export const azureADConfig: AzureADConfig = new Proxy({} as AzureADConfig, {
  get(_, prop, receiver) {
    return Reflect.get(resolveConfig(), prop, receiver);
  },
  has(_, prop) {
    return prop in resolveConfig();
  },
  ownKeys() {
    return Reflect.ownKeys(resolveConfig());
  },
  getOwnPropertyDescriptor(_, prop) {
    return Object.getOwnPropertyDescriptor(resolveConfig(), prop);
  },
});

export function isAzureADConfigured(): boolean {
  const cfg = resolveConfig();
  return Boolean(cfg.tenantId && cfg.clientId);
}
